import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ApiService } from '@pos-app/core-ui';
import { LoadedGiftCard } from '@pos-app/data';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { NgIf, DecimalPipe, AsyncPipe } from '@angular/common';
import { CoreUiModule } from '../../../../../../../../libs/core-ui/src/lib/core-ui.module';

@Component({
    selector: 'app-orders-gift-card',
    templateUrl: './orders-gift-card.component.html',
    styleUrls: ['./orders-gift-card.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, NgIf, CoreUiModule, DecimalPipe, AsyncPipe]
})
export class OrdersGiftCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() _id: string;
  @Input() isPayment?: boolean;
  @Input() loadAmount: number;
  @Input() outstanding: number;
  @Input() loadedCard?: LoadedGiftCard;
  @Input() orderNumber?: string;
  @Input() showCancel: boolean

  @Output() paymentDetails = new EventEmitter<any>();

  giftCardForm: UntypedFormGroup;
  unSubcribe$ = new Subject<void>();
  private _isFetchingSubject = new BehaviorSubject<boolean>(false);
  isFetching$ = this._isFetchingSubject.asObservable();
  private _isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this._isLoadingSubject.asObservable();

  get amountNeedToPaid(): number {
    const value = (this.loadAmount || 0) - (this.giftCardForm?.controls['amount']?.value || 0);

    return value > 0 ? value : 0;
  }

  constructor(private fb: UntypedFormBuilder, private apiService: ApiService, private toastr: ToastrService) {}

  ngOnInit() {
    this.giftCardForm = this.fb.group({
      cardNumber: [this.loadedCard?.giftcardNumber || '', Validators.required],
      amount: [this.loadAmount !== null ? Number(this.loadAmount) : ''],
      receipt: [
        {
          value: this.loadedCard?.receipt || '',
          disabled: true,
        },
      ],
      cardBalance: [''],
      response: [{ value: this.loadedCard?.responseFromVendor || '', disabled: true }],
      pin: [''],
    });

    if (this.isPayment) {
      this.giftCardForm.get('pin').setValidators([Validators.required]);
      this.giftCardForm.get('cardBalance').setValidators([Validators.required]);
    } else if (this.loadedCard) {
      this.giftCardForm.disable();
    }

    this.giftCardForm
      .get('cardNumber')
      .valueChanges.pipe(takeUntil(this.unSubcribe$))
      .subscribe(() => {
        this.giftCardForm.patchValue({ cardBalance: '' });
      });

    this.giftCardForm.valueChanges.pipe(takeUntil(this.unSubcribe$)).subscribe(() => this.updateAmountMaxValidator());
    this.updateAmountMaxValidator();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.outstanding && this.giftCardForm) {
      this.updateAmountMaxValidator();
    }
  }

  /* payment can't exceed the outstanding amount or the card balance; ignore limits that aren't known yet ('' / null) */
  private updateAmountMaxValidator() {
    const amountControl = this.giftCardForm.get('amount');
    const cardBalance = this.giftCardForm.get('cardBalance').value;
    const limits = (this.isPayment ? [this.outstanding, cardBalance] : [])
      .filter((limit) => limit !== null && limit !== undefined && limit !== '')
      .map(Number);

    amountControl.setValidators(limits.length ? [Validators.max(Math.min(...limits))] : []);
    amountControl.updateValueAndValidity({ emitEvent: false });
  }

  ngOnDestroy() {
    this.unSubcribe$.next();
    this.unSubcribe$.complete();
  }

  fetchCardBalance() {
    this._isFetchingSubject.next(true);
    this.giftCardForm.patchValue({ cardBalance: '' });
    this.apiService
      .fetchCardBalance({
        cardID: this.giftCardForm.get('cardNumber').value,
      })
      .pipe(
        finalize(() => this._isFetchingSubject.next(false)),
        takeUntil(this.unSubcribe$)
      )
      .subscribe(
        (res) => {
          if (res) {
            this.giftCardForm.patchValue(
              {
                cardBalance: res.balance,
                response: res.message,
              },
              { emitEvent: false }
            );
            /* default to whatever is smaller: the amount still owing on the order or what's left on the card */
            if (this.isPayment) {
              this.giftCardForm.patchValue(
                {
                  amount: Math.min(Number(this.outstanding) || 0, Number(res.balance) || 0),
                },
                { emitEvent: false }
              );
            }
            this.updateAmountMaxValidator();
          }
        },
        (error) => {
          if (error?.ErrorStatus == 404) {
            this.giftCardForm.patchValue({
              cardBalance: null,
            });
          } else {
            this.toastr.error(error?.ErrorMessage);
          }
        }
      );
  }

  cardTransaction() {
    this._isLoadingSubject.next(true);
    const transaction = this.isPayment
      ? this.apiService.redeemCard({
          cardID: this.giftCardForm.get('cardNumber').value,
          paymentAmount: this.giftCardForm.get('amount').value,
          pin: this.giftCardForm.get('pin').value,
        })
      : this.apiService.givexActivation({
          cardID: this.giftCardForm.get('cardNumber').value,
          loadAmount: this.giftCardForm.get('amount').value,
          orderNumber: this.orderNumber,
        });

    transaction
      .pipe(
        finalize(() => this._isLoadingSubject.next(false)),
        takeUntil(this.unSubcribe$)
      )
      .subscribe(
        (res) => {
          this.giftCardForm.patchValue({ cardBalance: '' });
          if (res) {
            this.giftCardForm.patchValue({
              response: res.message,
              receipt: res.transactionID,
            });
            this.paymentDetails.emit({
              _id: this._id?.toString(),
              giftcardNumber: this.giftCardForm.get('cardNumber').value,
              reference: res.transactionID,
              responseFromVendor: res.message,
              amount: this.giftCardForm.get('amount').value,
            });
            if (!this.isPayment) {
              this.giftCardForm.get('cardNumber').disable();
            }
          }
        },
        (error) => {
          this.toastr.error(error?.ErrorMessage);
        }
      );
  }
}
